import React, { useState } from 'react';
import { Image, Type, AlignLeft, AlignCenter, AlignRight, Eye, EyeOff, Building, MapPin, Palette, Plus, X } from 'lucide-react';

const BrandingSettings = ({ settings, handleSettingChange }) => {
  
  // Local state for custom gradient builder
  const [showGradientBuilder, setShowGradientBuilder] = useState({
    companyName: false,
    businessRegNumber: false,
    address: false
  });

  const [gradientColors, setGradientColors] = useState({
    companyName: ['#fb923c', '#ef4444', '#9333ea'],
    businessRegNumber: ['#9ca3af'],
    address: ['#9ca3af']
  });

  const updateBrandingSetting = (key, value) => {
    const updatedBranding = {
      ...settings.branding,
      [key]: value
    };
    handleSettingChange('branding', updatedBranding);
  };

  // Preset color options
  const presetGradients = [
    { name: 'Orange Gradient', value: 'from-orange-400 via-red-500 to-purple-600', colors: ['#fb923c', '#ef4444', '#9333ea'] },
    { name: 'Blue Gradient', value: 'from-blue-400 via-cyan-500 to-teal-600', colors: ['#60a5fa', '#06b6d4', '#14b8a6'] },
    { name: 'Green Gradient', value: 'from-green-400 via-emerald-500 to-teal-600', colors: ['#4ade80', '#10b981', '#14b8a6'] },
    { name: 'Purple Gradient', value: 'from-purple-400 via-pink-500 to-red-600', colors: ['#c084fc', '#ec4899', '#dc2626'] },
    { name: 'Gold Gradient', value: 'from-yellow-400 via-orange-500 to-red-600', colors: ['#facc15', '#f97316', '#dc2626'] },
    { name: 'Red Gradient', value: 'from-red-400 via-rose-500 to-pink-600', colors: ['#f87171', '#f43f5e', '#db2777'] },
    { name: 'Indigo Gradient', value: 'from-indigo-400 via-purple-500 to-pink-600', colors: ['#818cf8', '#a855f7', '#db2777'] },
    { name: 'Sunset', value: 'from-orange-500 via-red-600 to-purple-700', colors: ['#f97316', '#dc2626', '#7e22ce'] },
    { name: 'Ocean', value: 'from-cyan-400 via-blue-500 to-indigo-600', colors: ['#22d3ee', '#3b82f6', '#4f46e5'] },
    { name: 'Forest', value: 'from-lime-400 via-green-500 to-emerald-600', colors: ['#a3e635', '#22c55e', '#059669'] }
  ];

  const presetSolidColors = [
    { name: 'White', value: 'text-white', hex: '#ffffff' },
    { name: 'Light Gray', value: 'text-gray-300', hex: '#d1d5db' },
    { name: 'Gray', value: 'text-gray-400', hex: '#9ca3af' },
    { name: 'Dark Gray', value: 'text-gray-500', hex: '#6b7280' },
    { name: 'Orange', value: 'text-orange-400', hex: '#fb923c' },
    { name: 'Blue', value: 'text-blue-400', hex: '#60a5fa' },
    { name: 'Green', value: 'text-green-400', hex: '#4ade80' },
    { name: 'Red', value: 'text-red-400', hex: '#f87171' },
    { name: 'Purple', value: 'text-purple-400', hex: '#c084fc' },
    { name: 'Yellow', value: 'text-yellow-400', hex: '#facc15' },
    { name: 'Pink', value: 'text-pink-400', hex: '#f472b6' },
    { name: 'Indigo', value: 'text-indigo-400', hex: '#818cf8' },
    { name: 'Teal', value: 'text-teal-400', hex: '#2dd4bf' },
    { name: 'Cyan', value: 'text-cyan-400', hex: '#22d3ee' },
    { name: 'Lime', value: 'text-lime-400', hex: '#a3e635' }
  ];

  const isGradient = (colorValue) => {
    return colorValue && (colorValue.includes('from-') || colorValue.startsWith('linear-gradient'));
  };

  const isCustomColor = (colorValue) => {
    return colorValue && (colorValue.startsWith('#') || colorValue.startsWith('rgb') || colorValue.startsWith('linear-gradient'));
  };

  // Convert hex to Tailwind-like class for preview
  const hexToPreviewStyle = (hex) => {
    return { color: hex };
  };

  // Create gradient from colors array
  const createGradientFromColors = (colors) => {
    if (colors.length === 1) {
      return colors[0];
    }
    return `linear-gradient(to right, ${colors.join(', ')})`;
  };

  // Handle gradient color change
  const handleGradientColorChange = (element, index, newColor) => {
    const updatedColors = [...gradientColors[element]];
    updatedColors[index] = newColor;
    setGradientColors(prev => ({
      ...prev,
      [element]: updatedColors
    }));
  };

  // Add gradient color
  const addGradientColor = (element) => {
    if (gradientColors[element].length < 5) {
      setGradientColors(prev => ({
        ...prev,
        [element]: [...prev[element], '#ffffff']
      }));
    }
  };

  // Remove gradient color
  const removeGradientColor = (element, index) => {
    if (gradientColors[element].length > 1) {
      const updatedColors = gradientColors[element].filter((_, i) => i !== index);
      setGradientColors(prev => ({
        ...prev,
        [element]: updatedColors
      }));
    }
  };

  // Apply custom gradient
  const applyCustomGradient = (element, settingKey) => {
    const gradient = createGradientFromColors(gradientColors[element]);
    updateBrandingSetting(settingKey, gradient);
    setShowGradientBuilder(prev => ({ ...prev, [element]: false }));
  };

  // Apply custom solid color
  const applyCustomSolidColor = (hex, settingKey) => {
    updateBrandingSetting(settingKey, hex);
  };

  // Color picker component for solid colors
  const ColorPickerSection = ({ title, currentColor, settingKey, showGradients = false, element = null }) => {
    const [customSolidColor, setCustomSolidColor] = useState(
      isCustomColor(currentColor) && !isGradient(currentColor) ? currentColor : '#fb923c'
    );

    return (
      <div>
        <label className="text-white font-medium mb-3 block flex items-center gap-2">
          <Palette size={18} className="text-orange-400" />
          {title}
        </label>

        {/* Preset Options */}
        <div className="space-y-4">
          
          {/* Preset Gradients (if enabled) */}
          {showGradients && (
            <>
              <div>
                <div className="text-sm text-gray-400 mb-2">Preset Gradients</div>
                <div className="grid grid-cols-3 gap-3">
                  {presetGradients.map((gradient) => (
                    <button
                      key={gradient.value}
                      onClick={() => {
                        updateBrandingSetting(settingKey, gradient.value);
                        setGradientColors(prev => ({
                          ...prev,
                          [element]: gradient.colors
                        }));
                      }}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        currentColor === gradient.value
                          ? 'border-orange-500 bg-orange-500/20 ring-2 ring-orange-500/50'
                          : 'border-gray-600 bg-gray-700/30 hover:border-gray-500'
                      }`}
                    >
                      <div 
                        className="w-full h-8 rounded mb-2"
                        style={{ background: `linear-gradient(to right, ${gradient.colors.join(', ')})` }}
                      ></div>
                      <div className="text-xs text-gray-300 text-center">{gradient.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Gradient Builder */}
              <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm text-gray-300 font-medium">Custom Gradient</div>
                  <button
                    onClick={() => setShowGradientBuilder(prev => ({ ...prev, [element]: !prev[element] }))}
                    className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1"
                  >
                    {showGradientBuilder[element] ? 'Hide Builder' : 'Show Builder'}
                  </button>
                </div>

                {showGradientBuilder[element] && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      {gradientColors[element].map((color, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div className="text-xs text-gray-400 w-16">Color {index + 1}:</div>
                          <input
                            type="color"
                            value={color}
                            onChange={(e) => handleGradientColorChange(element, index, e.target.value)}
                            className="w-12 h-8 rounded border border-gray-600 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={color}
                            onChange={(e) => handleGradientColorChange(element, index, e.target.value)}
                            className="flex-1 bg-gray-600 text-white text-xs px-2 py-1 rounded border border-gray-500"
                            placeholder="#000000"
                          />
                          {gradientColors[element].length > 1 && (
                            <button
                              onClick={() => removeGradientColor(element, index)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <X size={16} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {gradientColors[element].length < 5 && (
                      <button
                        onClick={() => addGradientColor(element)}
                        className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                      >
                        <Plus size={14} />
                        Add Color (max 5)
                      </button>
                    )}

                    {/* Gradient Preview */}
                    <div 
                      className="w-full h-12 rounded border border-gray-600"
                      style={{ background: createGradientFromColors(gradientColors[element]) }}
                    ></div>

                    <button
                      onClick={() => applyCustomGradient(element, settingKey)}
                      className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-red-700 text-sm"
                    >
                      Apply Custom Gradient
                    </button>
                  </div>
                )}

                {/* Show current gradient if it's custom */}
                {!showGradientBuilder[element] && isGradient(currentColor) && isCustomColor(currentColor) && (
                  <div 
                    className="w-full h-8 rounded border border-gray-600"
                    style={{ background: currentColor }}
                  ></div>
                )}
              </div>
            </>
          )}

          {/* Preset Solid Colors */}
          <div>
            <div className="text-sm text-gray-400 mb-2">Preset Solid Colors</div>
            <div className="grid grid-cols-5 gap-3">
              {presetSolidColors.map((color) => (
                <button
                  key={color.value}
                  onClick={() => updateBrandingSetting(settingKey, color.value)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    currentColor === color.value
                      ? 'border-orange-500 bg-orange-500/20 ring-2 ring-orange-500/50'
                      : 'border-gray-600 bg-gray-700/30 hover:border-gray-500'
                  }`}
                >
                  <div 
                    className="w-full h-8 rounded mb-2 border border-gray-600"
                    style={{ background: color.hex }}
                  ></div>
                  <div className="text-xs text-gray-300 text-center truncate">{color.name}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Solid Color Picker */}
          <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
            <div className="text-sm text-gray-300 font-medium mb-3">Custom Solid Color</div>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={customSolidColor}
                onChange={(e) => setCustomSolidColor(e.target.value)}
                className="w-16 h-12 rounded border border-gray-600 cursor-pointer"
              />
              <input
                type="text"
                value={customSolidColor}
                onChange={(e) => setCustomSolidColor(e.target.value)}
                className="flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500"
                placeholder="#000000"
              />
              <button
                onClick={() => applyCustomSolidColor(customSolidColor, settingKey)}
                className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-red-700 whitespace-nowrap"
              >
                Apply
              </button>
            </div>
            {isCustomColor(currentColor) && !isGradient(currentColor) && (
              <div className="mt-3">
                <div className="text-xs text-gray-400 mb-1">Current Custom Color:</div>
                <div 
                  className="w-full h-8 rounded border border-gray-600"
                  style={{ background: currentColor }}
                ></div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      
      {/* Company Logo Settings */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-700">
          <h4 className="text-lg font-semibold text-white flex items-center gap-2">
            <Image className="w-5 h-5 text-orange-400" />
            Company Logo Display
          </h4>
          <p className="text-gray-400 text-sm mt-1">
            Control how your company logo appears on the customer booking form
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          
          {/* Show/Hide Logo */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label className="text-white font-medium flex items-center gap-2">
                {settings.branding?.showLogo ? <Eye size={18} className="text-green-400" /> : <EyeOff size={18} className="text-gray-400" />}
                Show Company Logo
              </label>
              <p className="text-gray-400 text-sm mt-1">
                Display your company logo from MyAccount profile on the booking form
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.branding?.showLogo || false}
                onChange={(e) => updateBrandingSetting('showLogo', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
            </label>
          </div>

          {/* Logo Size */}
          {settings.branding?.showLogo && (
            <div>
              <label className="text-white font-medium mb-3 block">Logo Size</label>
              <div className="grid grid-cols-3 gap-3">
                {['small', 'medium', 'large'].map(size => (
                  <button
                    key={size}
                    onClick={() => updateBrandingSetting('logoSize', size)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      settings.branding?.logoSize === size
                        ? 'border-orange-500 bg-orange-500/20 text-orange-300'
                        : 'border-gray-600 bg-gray-700/30 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <div className="text-center">
                      <Image 
                        size={size === 'small' ? 24 : size === 'large' ? 40 : 32} 
                        className="mx-auto mb-2"
                      />
                      <div className="text-sm font-medium capitalize">{size}</div>
                      <div className="text-xs text-gray-500">
                        {size === 'small' ? '48px' : size === 'large' ? '96px' : '64px'}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Logo Position */}
          {settings.branding?.showLogo && (
            <div>
              <label className="text-white font-medium mb-3 block">Logo Position</label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'left', label: 'Left', icon: AlignLeft },
                  { value: 'center', label: 'Center', icon: AlignCenter },
                  { value: 'right', label: 'Right', icon: AlignRight }
                ].map(position => {
                  const Icon = position.icon;
                  return (
                    <button
                      key={position.value}
                      onClick={() => updateBrandingSetting('logoPosition', position.value)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        settings.branding?.logoPosition === position.value
                          ? 'border-orange-500 bg-orange-500/20 text-orange-300'
                          : 'border-gray-600 bg-gray-700/30 text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      <Icon size={20} className="mx-auto mb-2" />
                      <div className="text-sm font-medium">{position.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Company Name Settings */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-700">
          <h4 className="text-lg font-semibold text-white flex items-center gap-2">
            <Type className="w-5 h-5 text-orange-400" />
            Company Name Display
          </h4>
          <p className="text-gray-400 text-sm mt-1">
            Control how your company name appears on the customer booking form
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          
          {/* Show/Hide Company Name */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label className="text-white font-medium flex items-center gap-2">
                {settings.branding?.showCompanyName ? <Eye size={18} className="text-green-400" /> : <EyeOff size={18} className="text-gray-400" />}
                Show Company Name
              </label>
              <p className="text-gray-400 text-sm mt-1">
                Display your company name from MyAccount profile on the booking form
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.branding?.showCompanyName || false}
                onChange={(e) => updateBrandingSetting('showCompanyName', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
            </label>
          </div>

          {/* Company Name Color Picker */}
          {settings.branding?.showCompanyName && (
            <ColorPickerSection
              title="Company Name Color"
              currentColor={settings.branding?.nameColor || 'from-orange-400 via-red-500 to-purple-600'}
              settingKey="nameColor"
              showGradients={true}
              element="companyName"
            />
          )}

          {/* Company Name Size */}
          {settings.branding?.showCompanyName && (
            <div>
              <label className="text-white font-medium mb-3 block">Name Text Size</label>
              <div className="grid grid-cols-3 gap-3">
                {['small', 'medium', 'large'].map(size => (
                  <button
                    key={size}
                    onClick={() => updateBrandingSetting('nameSize', size)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      settings.branding?.nameSize === size
                        ? 'border-orange-500 bg-orange-500/20 text-orange-300'
                        : 'border-gray-600 bg-gray-700/30 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <div className="text-center">
                      <Type 
                        size={size === 'small' ? 20 : size === 'large' ? 32 : 26} 
                        className="mx-auto mb-2"
                      />
                      <div className="text-sm font-medium capitalize">{size}</div>
                      <div className="text-xs text-gray-500">
                        {size === 'small' ? '20px' : size === 'large' ? '36px' : '28px'}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Company Name Position */}
          {settings.branding?.showCompanyName && (
            <div>
              <label className="text-white font-medium mb-3 block">Name Position</label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'left', label: 'Left', icon: AlignLeft },
                  { value: 'center', label: 'Center', icon: AlignCenter },
                  { value: 'right', label: 'Right', icon: AlignRight }
                ].map(position => {
                  const Icon = position.icon;
                  return (
                    <button
                      key={position.value}
                      onClick={() => updateBrandingSetting('namePosition', position.value)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        settings.branding?.namePosition === position.value
                          ? 'border-orange-500 bg-orange-500/20 text-orange-300'
                          : 'border-gray-600 bg-gray-700/30 text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      <Icon size={20} className="mx-auto mb-2" />
                      <div className="text-sm font-medium">{position.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Business Registration Number Settings */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-700">
          <h4 className="text-lg font-semibold text-white flex items-center gap-2">
            <Building className="w-5 h-5 text-orange-400" />
            Business Registration Number
          </h4>
          <p className="text-gray-400 text-sm mt-1">
            Control business registration number display on the customer booking form
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Show/Hide Business Registration Number */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label className="text-white font-medium flex items-center gap-2">
                {settings.branding?.showBusinessRegNumber ? <Eye size={18} className="text-green-400" /> : <EyeOff size={18} className="text-gray-400" />}
                Show Business Registration Number
              </label>
              <p className="text-gray-400 text-sm mt-1">
                Display your business registration number from MyAccount profile on the booking form
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.branding?.showBusinessRegNumber || false}
                onChange={(e) => updateBrandingSetting('showBusinessRegNumber', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
            </label>
          </div>

          {/* Business Registration Number Color Picker */}
          {settings.branding?.showBusinessRegNumber && (
            <ColorPickerSection
              title="Registration Number Color"
              currentColor={settings.branding?.businessRegNumberColor || 'text-gray-400'}
              settingKey="businessRegNumberColor"
              showGradients={true}
              element="businessRegNumber"
            />
          )}
        </div>
      </div>

      {/* Address Settings */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-700">
          <h4 className="text-lg font-semibold text-white flex items-center gap-2">
            <MapPin className="w-5 h-5 text-orange-400" />
            Company Address
          </h4>
          <p className="text-gray-400 text-sm mt-1">
            Control company address display on the customer booking form
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Show/Hide Address */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label className="text-white font-medium flex items-center gap-2">
                {settings.branding?.showAddress ? <Eye size={18} className="text-green-400" /> : <EyeOff size={18} className="text-gray-400" />}
                Show Company Address
              </label>
              <p className="text-gray-400 text-sm mt-1">
                Display your company address from MyAccount profile on the booking form
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.branding?.showAddress || false}
                onChange={(e) => updateBrandingSetting('showAddress', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
            </label>
          </div>

          {/* Address Color Picker */}
          {settings.branding?.showAddress && (
            <ColorPickerSection
              title="Address Color"
              currentColor={settings.branding?.addressColor || 'text-gray-400'}
              settingKey="addressColor"
              showGradients={true}
              element="address"
            />
          )}
        </div>
      </div>

      {/* Preview Card */}
      <div className="bg-gradient-to-r from-blue-900/20 to-indigo-900/20 border border-blue-500/30 rounded-xl p-6">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <Eye className="w-5 h-5 text-blue-400" />
          Live Preview
        </h4>
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className={`${
            settings.branding?.logoPosition === 'left' ? 'text-left' :
            settings.branding?.logoPosition === 'right' ? 'text-right' :
            'text-center'
          }`}>
            {settings.branding?.showLogo && (
              <div className={`flex mb-4 ${
                settings.branding?.logoPosition === 'left' ? 'justify-start' :
                settings.branding?.logoPosition === 'right' ? 'justify-end' :
                'justify-center'
              }`}>
                <div className={`${
                  settings.branding?.logoSize === 'small' ? 'w-12 h-12' :
                  settings.branding?.logoSize === 'large' ? 'w-24 h-24' :
                  'w-16 h-16'
                } bg-gray-700 rounded-lg flex items-center justify-center`}>
                  <Image size={settings.branding?.logoSize === 'small' ? 24 : settings.branding?.logoSize === 'large' ? 48 : 32} className="text-gray-500" />
                </div>
              </div>
            )}
            {settings.branding?.showCompanyName && (
              <h2 
                className={`font-bold ${
                  settings.branding?.nameSize === 'small' ? 'text-xl' :
                  settings.branding?.nameSize === 'large' ? 'text-4xl' :
                  'text-2xl'
                }`}
                style={
                  isGradient(settings.branding?.nameColor)
                    ? {
                        background: isCustomColor(settings.branding?.nameColor) 
                          ? settings.branding?.nameColor 
                          : `linear-gradient(to right, #fb923c, #ef4444, #9333ea)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }
                    : { color: isCustomColor(settings.branding?.nameColor) ? settings.branding?.nameColor : '#ffffff' }
                }
              >
                Your Company Name
              </h2>
            )}
            {settings.branding?.showBusinessRegNumber && (
              <p 
                className="text-xs mt-2 flex items-center gap-1 justify-center"
                style={
                  isGradient(settings.branding?.businessRegNumberColor)
                    ? {
                        background: isCustomColor(settings.branding?.businessRegNumberColor) 
                          ? settings.branding?.businessRegNumberColor 
                          : `linear-gradient(to right, #9ca3af, #9ca3af)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }
                    : { color: isCustomColor(settings.branding?.businessRegNumberColor) ? settings.branding?.businessRegNumberColor : '#9ca3af' }
                }
              >
                <Building size={10} />
                Reg: BRN123456789
              </p>
            )}
            {settings.branding?.showAddress && (
              <p 
                className="text-xs mt-1"
                style={
                  isGradient(settings.branding?.addressColor)
                    ? {
                        background: isCustomColor(settings.branding?.addressColor) 
                          ? settings.branding?.addressColor 
                          : `linear-gradient(to right, #9ca3af, #9ca3af)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }
                    : { color: isCustomColor(settings.branding?.addressColor) ? settings.branding?.addressColor : '#9ca3af' }
                }
              >
                123 Business Street, City, Country
              </p>
            )}
            {!settings.branding?.showLogo && !settings.branding?.showCompanyName && !settings.branding?.showBusinessRegNumber && !settings.branding?.showAddress && (
              <p className="text-gray-500 italic">No branding elements enabled</p>
            )}
          </div>
        </div>
        <p className="text-gray-400 text-sm mt-3">
          This preview shows how your branding will appear on the customer booking form header
        </p>
      </div>

    </div>
  );
};

export default BrandingSettings;